import { INR } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Plus, Sparkles, ChevronRight } from "lucide-react";

export default function CombosSection({ combos }) {
  if (!combos || combos.length === 0) return null;

  return (
    <section
      id="combos"
      data-testid="combos-section"
      className="bg-[#0A0C11] border-y border-white/5"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
        <div className="max-w-2xl mb-10">
          <div className="text-xs uppercase tracking-[0.3em] text-[var(--accent-gold)] mb-3 flex items-center gap-2">
            <Sparkles size={14} />
            Curated for you
          </div>
          <h2 className="font-headings text-3xl sm:text-4xl lg:text-5xl font-light leading-tight">
            Combos for <em className="text-[var(--brand-gold)] not-italic">every</em> occasion & season.
          </h2>
          <p className="mt-4 text-sm sm:text-base text-[var(--text-secondary)] leading-relaxed">
            Hand-picked from the 100+ bottle library — each combo offers a Premium pair and a
            Middle Eastern counterpart, so you can try either tier without guessing.
          </p>
        </div>

        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-2">
          {combos.map((c, idx) => (
            <ComboCard key={c.slug} combo={c} index={idx} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ComboCard({ combo, index }) {
  return (
    <div
      data-testid={`combo-${combo.slug}`}
      className="reveal bg-[var(--bg-surface)] border border-[var(--border-default)] hover:border-[var(--border-hover)] transition-colors overflow-hidden"
      style={{ animationDelay: `${Math.min(index, 6) * 60}ms` }}
    >
      <div className="p-5 sm:p-6 border-b border-[var(--border-default)]">
        <div className="flex items-center gap-3 mb-2">
          <span className="text-2xl">{combo.emoji}</span>
          <div>
            <h3 className="font-headings text-xl sm:text-2xl text-[var(--text-primary)] leading-tight">
              {combo.title}
            </h3>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[var(--accent-gold)]/80 mt-0.5">
              {combo.occasion} · {combo.season}
            </div>
          </div>
        </div>
        <p className="text-sm text-[var(--text-secondary)] leading-relaxed mt-3">
          {combo.blurb}
        </p>
      </div>

      <div className="grid sm:grid-cols-2 divide-x divide-[var(--border-default)]">
        <ComboColumn label="Premium" products={combo.premium} accent="premium" />
        <ComboColumn label="Middle Eastern" products={combo.middle_eastern} accent="me" />
      </div>
    </div>
  );
}

function ComboColumn({ label, products, accent }) {
  const accentText =
    accent === "premium"
      ? "text-[var(--cat-pr-text)]"
      : "text-[var(--cat-me-text)]";
  return (
    <div className="p-4 sm:p-5 space-y-2.5">
      <div className={`text-[10px] uppercase tracking-[0.25em] ${accentText} mb-1`}>
        {label}
      </div>
      {products.length === 0 ? (
        <div className="text-xs text-[var(--text-muted)] italic py-2">No pick available</div>
      ) : (
        products.map((p) => <ComboPickRow key={p.id} product={p} />)
      )}
    </div>
  );
}

function ComboPickRow({ product }) {
  const { addItem } = useCart();
  const stock = product.stock_ml ?? 0;
  const soldOut = stock < 5;

  const scrollToProduct = () => {
    const el = document.querySelector(`[data-testid="product-card-${product.id}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-[var(--brand-gold)]");
      setTimeout(() => el.classList.remove("ring-2", "ring-[var(--brand-gold)]"), 1800);
    }
  };

  return (
    <div
      data-testid={`combo-pick-${product.id}`}
      className="group flex items-center gap-3 py-2 border-b border-[var(--border-default)]/60 last:border-b-0"
    >
      <button
        onClick={scrollToProduct}
        className="flex-1 text-left hover:text-[var(--brand-gold)] transition-colors flex items-start gap-1"
      >
        <span>
          <span className="text-[10px] uppercase tracking-[0.2em] text-[var(--text-muted)] block">
            {product.brand}
          </span>
          <span className="font-headings text-sm leading-snug">{product.name}</span>
        </span>
        <ChevronRight
          size={14}
          className="opacity-0 group-hover:opacity-100 text-[var(--brand-gold)] mt-1.5 transition-opacity"
        />
      </button>
      <div className="text-right shrink-0">
        <div className="font-headings text-sm text-[var(--brand-gold)]">
          {INR(product.price_5ml)}
        </div>
        <button
          data-testid={`combo-add-${product.id}`}
          disabled={soldOut}
          onClick={() => !soldOut && addItem(product, "5ml")}
          title={soldOut ? "Sold out" : "Add 5ml to cart"}
          className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-1 border border-[var(--border-default)] hover:border-[var(--brand-gold)] hover:text-[var(--brand-gold)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Plus size={10} />
          {soldOut ? "Out" : "5ml"}
        </button>
      </div>
    </div>
  );
}
